import React, { useEffect, useState } from 'react';
import { ExternalLink, LoaderCircle, ReceiptText, X } from 'lucide-react';

interface ReceiptModalProps {
    isOpen: boolean;
    onClose: () => void;
    receiptUrl: string;
}

const RECEIPT_CDN_ORIGIN = 'https://uniprime-axiom.sfo3.cdn.digitaloceanspaces.com';
const RECEIPT_PATH_PREFIX = '/receipts/';

const getReceiptPreviewUrl = (receiptUrl: string): string => {
    try {
        const url = new URL(receiptUrl);
        const isTrustedReceipt = url.origin === RECEIPT_CDN_ORIGIN
            && /^\/receipts\/[A-Za-z0-9/_-]+\.pdf$/.test(url.pathname);

        if (isTrustedReceipt) {
            return `/receipt-files/${url.pathname.slice(RECEIPT_PATH_PREFIX.length)}${url.search}`;
        }
    } catch {
        // Relative receipt URLs can already be fetched from the current origin.
    }

    return receiptUrl;
};

export const ReceiptModal: React.FC<ReceiptModalProps> = ({
    isOpen,
    onClose,
    receiptUrl,
}) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) return;

        const controller = new AbortController();
        let objectUrl: string | null = null;

        const loadReceipt = async () => {
            setPreviewUrl(null);
            setPreviewError(null);

            try {
                const response = await fetch(getReceiptPreviewUrl(receiptUrl), {
                    signal: controller.signal,
                    credentials: 'same-origin',
                });

                if (!response.ok) {
                    throw new Error(`Receipt preview returned ${response.status}`);
                }

                const receipt = await response.blob();
                if (receipt.type && receipt.type !== 'application/pdf') {
                    throw new Error('Receipt preview did not return a PDF');
                }

                objectUrl = URL.createObjectURL(receipt);
                setPreviewUrl(objectUrl);
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') return;
                setPreviewError('The receipt preview could not be loaded.');
            }
        };

        loadReceipt();

        return () => {
            controller.abort();
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [isOpen, receiptUrl]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex h-full w-full items-center justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="receipt-modal-title"
        >
            <div className="relative flex h-[90vh] w-full max-w-5xl flex-col">
                <div className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                                <ReceiptText className="h-5 w-5" aria-hidden="true" />
                            </span>
                            <div>
                                <h3 id="receipt-modal-title" className="text-lg font-semibold text-slate-950">Receipt</h3>
                                <p className="text-xs text-slate-500">Completed service record</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            aria-label="Close receipt"
                        >
                            <X className="h-4 w-4" aria-hidden="true" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="flex flex-1 flex-col items-center justify-center overflow-hidden bg-slate-100 p-3 sm:p-4">
                        {!previewUrl && !previewError && (
                            <div className="flex flex-col items-center gap-3 text-sm text-slate-600" role="status">
                                <LoaderCircle className="h-7 w-7 animate-spin text-blue-600" aria-hidden="true" />
                                Loading receipt preview…
                            </div>
                        )}
                        {previewError && (
                            <div className="max-w-sm rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-center text-sm text-amber-900" role="alert">
                                {previewError} Use “Open in new tab” below to view the PDF.
                            </div>
                        )}
                        {previewUrl && (
                            <iframe
                                src={previewUrl}
                                className="h-full w-full rounded-lg border-0 bg-white shadow-sm"
                                title="Receipt Viewer"
                            />
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col-reverse gap-3 border-t border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-xs text-slate-500">If the preview does not load, open the PDF directly.</p>
                        <div className="flex items-center justify-end gap-3">
                            <a
                                href={receiptUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-10 items-center justify-center rounded-xl border border-blue-200 bg-blue-50 px-4 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            >
                                <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
                                Open in new tab
                            </a>
                            <button
                                onClick={onClose}
                                type="button"
                                className="inline-flex h-10 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
